
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Planning your integration',
  layout: 'guide',
  description: 'Design decisions that shape your OAuth implementation',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Planning your integration' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "architecture-and-token-model"
      }}>{`Architecture and token model`}</h2>
      <h3 {...{
        "id": "the-four-party-model"
      }}>{`The four-party model`}</h3>
      <p>{`Core Exchange connects four players:`}</p>
      <Image src='/assets/img/core-exchange/oauth-01.png' alt='Four-party architecture' mdxType="Image" />
      <p>{`Each party plays a role. The consumer uses an app, the app connects via Plaid, Plaid communicates with you, and you authenticate the user and issue tokens.`}</p>
      <h3 {...{
        "id": "user-scoped-access-token-architecture-recommended"
      }}>{`User-scoped access token architecture (recommended)`}</h3>
      <p>{`Your authorization server issues `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/#token-models-and-types-the-what"
        }}>{`one user-scoped token`}</a>{` to Plaid, and Plaid issues short-lived, app-scoped tokens to each connected app downstream.`}</p>
      <p><strong parentName="p">{`Why this matters:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Reduces API traffic`}</strong>{` - One token for multiple apps eliminates duplicate data requests for users with multiple connections.`}</li>
        <li parentName="ul"><strong parentName="li">{`Centralized consent management`}</strong>{` - Users revoke access from one place. Data Providers get a unified view through `}<a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`Consent management`}</a>{`.`}</li>
        <li parentName="ul"><strong parentName="li">{`Returning user experience enabled`}</strong>{` - Users authenticate once with your bank, then quickly reconnect to new apps using phone verification.`}</li>
      </ul>
      <h3 {...{
        "id": "the-returning-user-experience"
      }}>{`The returning user experience`}</h3>
      <p>{`The `}<a parentName="p" {...{
          "href": "/docs/link/returning-user/"
        }}>{`returning user experience`}</a>{` is Plaid's streamlined onboarding for users who have previously connected their accounts. Here's how it works:`}</p>
      <ol>
        <li parentName="ol">{`First connection: User verifies their phone number during the initial Link session`}</li>
        <li parentName="ol">{`Subsequent connections: When the same user connects to a new app, Plaid recognizes their phone number`}</li>
        <li parentName="ol">{`Quick reconnection: User can instantly access previously connected institutions and choose accounts to share - no need to re-authenticate`}</li>
      </ol>
      <p>{`Why it matters to Data Providers:`}</p>
      <ul>
        <li parentName="ul">{`Higher conversion rates - Users complete connections faster with less friction`}</li>
        <li parentName="ul">{`Reduced authentication load - Users can connect new apps without going through your full OAuth flow every time (when adding apps, not new accounts)`}</li>
        <li parentName="ul">{`Better user experience - Less manual data entry and faster onboarding`}</li>
      </ul>
      <p>{`This feature is automatically enabled when you issue one token to Plaid for each user.`}</p>
      <h3 {...{
        "id": "account-selection-where-to-place-the-screen"
      }}>{`Account selection: where to place the screen?`}</h3>
      <p>{`Where users select which accounts to share matters more than you think. This decision directly affects the user experience for returning users.`}</p>
      <h4 {...{
        "id": "plaid-owned-account-selection-recommended"
      }}>{`Plaid-owned account selection (recommended)`}</h4>
      <p>{`Users see the account selection screen in Plaid Link after authenticating with you. You issue a token with access to all accounts, and Plaid handles filtering based on the user's selection.`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Full returning user experience`}</strong>{` - Users authenticate once, then quickly connect new apps using phone verification`}</li>
        <li parentName="ul"><strong parentName="li">{`Flexible connections`}</strong>{` - Users can share different accounts with different apps without re-authenticating`}</li>
        <li parentName="ul"><strong parentName="li">{`Simpler implementation`}</strong>{` - You don't need account selection logic in your OAuth flow`}</li>
      </ul>
      <h4 {...{
        "id": "data-provider-owned-account-selection-partially-enabled"
      }}>{`Data Provider-owned account selection (partially enabled)`}</h4>
      <p>{`Users select accounts during the OAuth flow on your domain. The token you issue is scoped to only the selected accounts.`}</p>
      <p>{`If a user previously selected their checking account when connecting Venmo, they can later connect Copilot to that same checking account using the returning user experience. But if they want to share their savings account with Copilot (an account they never selected before), they'll need to go through the full OAuth flow again to expand the scope.`}</p>
      <p><strong parentName="p">{`When this happens:`}</strong></p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Scenario`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Previously selected?`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Flow`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`User connects a new app to an already-shared account`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Returning user experience`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`User wants to share a new account with any app`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Full OAuth flow required`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`Users don't think in terms of "previously selected accounts." They just want to connect their bank to an app. Forcing OAuth for a new account feels like friction, not security.`}</p>
      <p><strong parentName="p">{`Why Plaid recommends Plaid Link for account selection:`}</strong>{` Plaid Link delivers pre-built, conversion-optimized account selection screens with no development work required on your end. Link achieves industry-leading conversion rates through streamlined UIs, passkey authentication for returning users, and continuous optimization across 12,000+ institutions. If your users typically have multiple accounts (checking, savings, credit cards), placing account selection in Link eliminates the need to build and maintain your own account selection logic while delivering a better user experience.`}</p>
      <h3 {...{
        "id": "data-type-selection-and-compliance"
      }}>{`Data type selection and compliance`}</h3>
      <p>{`Some Data Providers display which data types the user is authorizing (account numbers, transactions, balances, etc.) during the OAuth flow. Transparency is important. But allowing users to deselect individual data types at the OAuth layer doesn't provide the security benefit you might expect - and it breaks the returning user experience.`}</p>
      <h4 {...{
        "id": "how-plaid-enforces-data-minimization"
      }}>{`How Plaid enforces data minimization`}</h4>
      <p>{`With the user-scoped access token model, you issue a broad token to Plaid that covers all data types the user might share with any app. Plaid then acts as a gatekeeper, issuing downstream app-specific tokens scoped only to what each app actually requested.`}</p>
      <p><strong parentName="p">{`Example:`}</strong>{` Even though your Plaid token includes statements, Venmo never sees them if Venmo only requests account numbers for ACH. The lending app that needs statements gets them, but Venmo doesn't. Data minimization happens at the Plaid layer, where apps declare their needs upfront.`}</p>
      <h4 {...{
        "id": "why-per-data-type-selection-at-the-oauth-layer-adds-friction-without-adding-security"
      }}>{`Why per-data-type selection at the OAuth layer adds friction without adding security`}</h4>
      <p>{`If users can deselect data types during OAuth, they'd need to re-authenticate every time they connect an app that requires a previously excluded data type. A user who opted out of sharing statements with Venmo would have to go through full OAuth again to connect a lending app that needs statements - even though Venmo never would have received those statements anyway.`}</p>
      <h4 {...{
        "id": "what-works-disclosure-without-selection"
      }}>{`What works: disclosure without selection`}</h4>
      <p>{`Show users what data types are shared, but don't give them checkboxes to deselect individual types. Users see what's shared and can approve or cancel the entire request.`}</p>
      <p>{`If you disclose data types during OAuth, show the superset of data types that Plaid might need across all use cases. Users authorize once, and Plaid handles scoping to individual apps based on what each app actually requested.`}</p>
      <h2 {...{
        "id": "application-registration"
      }}>{`Application registration`}</h2>
      <p>{`Plaid manages an extensive third-party risk management program and onboards apps to the network. Data Providers need visibility into which apps users are connecting to them. That's where App Directory comes in.`}</p>
      <h3 {...{
        "id": "app-directory-recommended"
      }}>{`App Directory (recommended)`}</h3>
      <p>{`App Directory provides Data Providers with application metadata without the overhead of Dynamic Client Registration. Available via APIs and a no-code dashboard, it's the fastest path to app-level visibility.`}</p>
      <p><strong parentName="p">{`Two endpoints available:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`GET /fdx/recipients`}</inlineCode></strong>{` - Returns a list of all applications`}</li>
        <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`GET /fdx/recipient/<recipient_id>`}</inlineCode></strong>{` - Returns details for a specific application`}</li>
      </ul>
      <p><strong parentName="p">{`What you get:`}</strong></p>
      <p>{`Each application record includes its name, logo, legal entity name, category, the date it joined Plaid's network, and how many of your customers are connected to it. See the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/permissions-manager/app-directory"
        }}>{`App Directory API`}</a>{` for the full field list.`}</p>
      <p><strong parentName="p">{`How to integrate:`}</strong></p>
      <ol>
        <li parentName="ol"><strong parentName="li">{`No-code option:`}</strong>{` Use Plaid's Data Partner Dashboard to search by application name or browse the full catalog with no development required.`}</li>
        <li parentName="ol"><strong parentName="li">{`API option:`}</strong>{` Integrate the App Directory endpoints into your systems for programmatic access.`}</li>
      </ol>
      <p><strong parentName="p">{`FDX alignment:`}</strong>{` This approach implements FDX RFC 0206 (Delegated App Registration), approved in FDX v5.1. Plaid handles app onboarding and due diligence. Data Providers get visibility and control without managing individual client registrations.`}</p>
      <p>{`If you also serve aggregators or third parties that use per-app Dynamic Client Registration, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/supporting-non-plaid-traffic"
        }}>{`Supporting non-Plaid traffic`}</a>{` for how the two client models coexist on the same API.`}</p>
      <h2 {...{
        "id": "planning-checklist"
      }}>{`Planning checklist`}</h2>
      <p>{`Before moving to implementation, make these key decisions:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Account selection screen location:`}</strong>{` Plaid Link or your domain?`}</li>
        <li parentName="ul"><strong parentName="li">{`Data type selection:`}</strong>{` Disclosure without checkboxes`}</li>
        <li parentName="ul"><strong parentName="li">{`OIDC vs OAuth 2.0:`}</strong>{` OIDC recommended for returning user experience`}</li>
      </ul>
      <p><strong parentName="p">{`Recommended configuration:`}</strong>{` Plaid-owned account selection + App Directory + OIDC`}</p>
      <p>{`This combination delivers:`}</p>
      <ul>
        <li parentName="ul">{`Full returning user experience`}</li>
        <li parentName="ul">{`Reduced API traffic for users with multiple app connections`}</li>
        <li parentName="ul">{`Simplified implementation`}</li>
        <li parentName="ul">{`Best user experience`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "architecture-and-token-model",
        level: 2,
        title: "Architecture and token model",
      },
{
        id: "the-four-party-model",
        level: 3,
        title: "The four-party model",
      },
{
        id: "user-scoped-access-token-architecture-recommended",
        level: 3,
        title: "User-scoped access token architecture (recommended)",
      },
{
        id: "the-returning-user-experience",
        level: 3,
        title: "The returning user experience",
      },
{
        id: "account-selection-where-to-place-the-screen",
        level: 3,
        title: "Account selection: where to place the screen?",
      },
{
        id: "plaid-owned-account-selection-recommended",
        level: 4,
        title: "Plaid-owned account selection (recommended)",
      },
{
        id: "data-provider-owned-account-selection-partially-enabled",
        level: 4,
        title: "Data Provider-owned account selection (partially enabled)",
      },
{
        id: "data-type-selection-and-compliance",
        level: 3,
        title: "Data type selection and compliance",
      },
{
        id: "how-plaid-enforces-data-minimization",
        level: 4,
        title: "How Plaid enforces data minimization",
      },
{
        id: "why-per-data-type-selection-at-the-oauth-layer-adds-friction-without-adding-security",
        level: 4,
        title: "Why per-data-type selection at the OAuth layer adds friction without adding security",
      },
{
        id: "what-works-disclosure-without-selection",
        level: 4,
        title: "What works: disclosure without selection",
      },
{
        id: "application-registration",
        level: 2,
        title: "Application registration",
      },
{
        id: "app-directory-recommended",
        level: 3,
        title: "App Directory (recommended)",
      },
{
        id: "planning-checklist",
        level: 2,
        title: "Planning checklist",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
